import CryptoJS from 'crypto-js';
import { secretKey } from '../../config/config';

const MASTER_SECRET_KEY = secretKey || 'mySuperSecretKey123!';

/**
 * Derive a unique 256-bit cryptographic key for a specific conversation.
 * If no conversationId is provided, returns the master secret key.
 */
export const getConversationKey = (conversationId) => {
  if (!conversationId) return MASTER_SECRET_KEY;
  try {
    return CryptoJS.SHA256(`${MASTER_SECRET_KEY}:${conversationId}`).toString();
  } catch (err) {
    return MASTER_SECRET_KEY;
  }
};

/**
 * Checks if a string is already encrypted with AES (OpenSSL Salted__ format in base64).
 */
export const isEncrypted = (text) => {
  if (typeof text !== 'string' || !text) return false;
  return text.startsWith('U2FsdGVkX1');
};

/**
 * Encrypt a plaintext chat message string using End-to-End AES-256 encryption.
 * @param {string} text - Plain text message
 * @param {string} [conversationId] - Optional conversation ID for conversation-specific key derivation
 * @returns {string} Encrypted ciphertext string (e.g., U2FsdGVkX1...)
 */
export const encryptMessage = (text, conversationId = null) => {
  if (text === null || text === undefined) return text;
  if (typeof text !== 'string') text = String(text);
  if (!text) return text;
  
  // If already encrypted, return as-is
  if (isEncrypted(text)) return text;

  try {
    const key = getConversationKey(conversationId);
    return CryptoJS.AES.encrypt(text, key).toString();
  } catch (err) {
    console.error('[E2EE] Client encryption error:', err);
    return text;
  }
};

/**
 * Decrypt a chat message ciphertext string using End-to-End AES-256 encryption.
 * Automatically tries conversation-derived key first, then master key fallback.
 * If ciphertext is already plaintext (or legacy message), returns original string gracefully.
 * @param {string} ciphertext - Encrypted ciphertext or plaintext string
 * @param {string} [conversationId] - Optional conversation ID
 * @returns {string} Decrypted plaintext message
 */
export const decryptMessage = (ciphertext, conversationId = null) => {
  if (ciphertext === null || ciphertext === undefined) return ciphertext;
  if (typeof ciphertext !== 'string' || !ciphertext) return ciphertext;

  // If it doesn't look like ciphertext, return directly
  if (!isEncrypted(ciphertext)) {
    return ciphertext;
  }

  // 1. Try decrypting with conversation-specific derived key
  if (conversationId) {
    try {
      const convKey = getConversationKey(conversationId);
      const bytes = CryptoJS.AES.decrypt(ciphertext, convKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      if (decrypted && decrypted.length > 0) {
        return decrypted;
      }
    } catch (err) {
      // Continue to master key fallback
    }
  }

  // 2. Try decrypting with master secret key
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, MASTER_SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    if (decrypted && decrypted.length > 0) {
      return decrypted;
    }
  } catch (err) {
    // Continue to return original
  }

  return ciphertext;
};
